import {
  CartLinesUpdateEvent,
  StandardEvents,
} from '@shopify/events';

const SANDING_VARIANT_ID = 57160145469815;
const PRIMING_VARIANT_ID = 57160146682231;

const HYPALON_HANDLES = [
  'hypalon-fabric-866-1500gsm',
  'hypalon-828-1300gsm',
];

let syncing = false;
let syncTimer = null;

function requiredServiceQuantity(metres) {
  return metres > 0 ? Math.ceil(metres / 6) : 0;
}

function isHypalonItem(item) {
  return HYPALON_HANDLES.includes(item.handle);
}

async function getCart() {
  const response = await fetch('/cart.js', {
    headers: {
      Accept: 'application/json',
    },
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error('Unable to read the Shopify cart.');
  }

  return response.json();
}

async function removeServiceVariant(variantId) {
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({
      updates: {
        [variantId]: 0,
      },
    }),
  });

  if (!response.ok) {
    throw new Error('Unable to remove the service product.');
  }

  return response.json();
}

async function addServiceVariant(
  variantId,
  quantity,
  description
) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({
      id: variantId,
      quantity,
      properties: {
        Details: description,
      },
    }),
  });

  if (!response.ok) {
    throw new Error('Unable to add the service product.');
  }

  return response.json();
}

function calculateRequiredServices(cart) {
  let sandingMetres = 0;
  let primingMetres = 0;

  let currentSandingQuantity = 0;
  let currentPrimingQuantity = 0;

  let currentSandingDetails = '';
  let currentPrimingDetails = '';

  for (const item of cart.items) {
    if (item.variant_id === SANDING_VARIANT_ID) {
      currentSandingQuantity += item.quantity;
      currentSandingDetails =
        item.properties?.Details || currentSandingDetails;
    }

    if (item.variant_id === PRIMING_VARIANT_ID) {
      currentPrimingQuantity += item.quantity;
      currentPrimingDetails =
        item.properties?.Details || currentPrimingDetails;
    }

    if (!isHypalonItem(item)) continue;

    if (item.properties?.Sanding === 'Yes') {
      sandingMetres += item.quantity;
    }

    if (item.properties?.Priming === 'Yes') {
      primingMetres += item.quantity;
    }
  }

  return {
    currentSandingQuantity,
    currentPrimingQuantity,
    currentSandingDetails,
    currentPrimingDetails,

    requiredSandingQuantity:
      requiredServiceQuantity(sandingMetres),

    requiredPrimingQuantity:
      requiredServiceQuantity(primingMetres),

    requiredSandingDetails:
      `Sanding service for ${sandingMetres} metres of Hypalon`,

    requiredPrimingDetails:
      `Priming service for ${primingMetres} metres of Hypalon`,
  };
}

async function synchroniseHypalonServices() {
  if (syncing) return null;

  syncing = true;

  try {
    const cart = await getCart();
    const state = calculateRequiredServices(cart);

    const sandingNeedsUpdate =
      state.currentSandingQuantity !==
        state.requiredSandingQuantity ||
      (
        state.requiredSandingQuantity > 0 &&
        state.currentSandingDetails !==
          state.requiredSandingDetails
      );

    const primingNeedsUpdate =
      state.currentPrimingQuantity !==
        state.requiredPrimingQuantity ||
      (
        state.requiredPrimingQuantity > 0 &&
        state.currentPrimingDetails !==
          state.requiredPrimingDetails
      );

    if (!sandingNeedsUpdate && !primingNeedsUpdate) {
      return null;
    }

    if (sandingNeedsUpdate) {
      await removeServiceVariant(SANDING_VARIANT_ID);

      if (state.requiredSandingQuantity > 0) {
        await addServiceVariant(
          SANDING_VARIANT_ID,
          state.requiredSandingQuantity,
          state.requiredSandingDetails
        );
      }
    }

    if (primingNeedsUpdate) {
      await removeServiceVariant(PRIMING_VARIANT_ID);

      if (state.requiredPrimingQuantity > 0) {
        await addServiceVariant(
          PRIMING_VARIANT_ID,
          state.requiredPrimingQuantity,
          state.requiredPrimingDetails
        );
      }
    }

    return getCart();
  } catch (error) {
    console.error('Hypalon service sync failed:', error);
    return null;
  } finally {
    syncing = false;
  }
}

function dispatchCartRefresh(cart) {
  const deferredPromise =
    CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: deferredPromise.promise,
    })
  );

  deferredPromise.resolve({
    cart:
      CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      itemCount: cart.item_count,
      source: 'hypalon-services',
      sourceId: 'hypalon-services',
      didError: false,
    },
  });
}

function lockServiceLines() {
  document
    .querySelectorAll(
      'a[href*="variant=57160145469815"],' +
      'a[href*="variant=57160146682231"]'
    )
    .forEach((link) => {
      const cartItem =
        link.closest('tr') ||
        link.closest('.cart-item') ||
        link.closest('[id^="CartItem"]') ||
        link.closest('[class*="cart-item"]');

      if (!cartItem) return;

      cartItem
        .querySelectorAll(
          'quantity-input,' +
          'quantity-selector-component,' +
          '.quantity,' +
          '.cart-item__quantity,' +
          '.cart-item__quantity-wrapper,' +
          'cart-remove-button,' +
          '.cart-remove-button'
        )
        .forEach((element) => {
          element.style.display = 'none';
        });
    });
}

function scheduleServiceSync(delay = 500) {
  clearTimeout(syncTimer);

  syncTimer = window.setTimeout(async () => {
    const updatedCart =
      await synchroniseHypalonServices();

    if (updatedCart) {
      dispatchCartRefresh(updatedCart);
    }

    window.setTimeout(lockServiceLines, 300);
    window.setTimeout(lockServiceLines, 900);
  }, delay);
}

document.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.source === 'hypalon-services') {
          return;
        }

        scheduleServiceSync(300);
      })
      .catch(() => {});
  }
);

document.addEventListener('DOMContentLoaded', () => {
  scheduleServiceSync(400);
  window.setTimeout(lockServiceLines, 800);
});